import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons"
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Collapse,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Typography,
} from "antd"
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { Link, Prompt, useHistory, useParams } from "react-router-dom"
import { useRegulatorySession as useAuthContext } from "../../providers/regulatoryProvider"
import type {
  CatalogInclude,
  CatalogRevision,
  CatalogTemplate,
  PointDefinition,
  TemplateDefinition,
} from "../../types/regulatory"
import {
  catalogPermissions,
  emptyDefinition,
  makeCatalogWrite,
  moveItem,
  newPoint,
  scopeLabels,
  validateDefinition,
} from "../../utils/regulatory"
import { RegulatoryApiError, allCatalogPages, regulatoryApi } from "../../utils/regulatoryApi"
import { CatalogFrame, NatureTag, ResourceState } from "./components/CatalogUI"
import { RevisionComparison } from "./components/RevisionComparison"
import { useCatalogResource } from "./useCatalog"
function Field({ label, children, full }: { label: string; children: ReactNode; full?: boolean }) {
  return (
    <div className={`regulatory-field${full ? " regulatory-form-full" : ""}`}>
      <Typography.Text strong>{label}</Typography.Text>
      {children}
    </div>
  )
}
const natureOptions = [
  "Exigence",
  "Recommandation",
  "Information",
  "Réserve d’interprétation",
  "Disposition nationale citée",
  "Complément non fourni",
  "Hors pièce jointe",
  "Contrôle personnalisé",
].map((value) => ({ label: value, value }))
function PointEditor({
  point,
  onSave,
  onCancel,
  packs,
}: {
  point: PointDefinition | null
  onSave: (point: PointDefinition) => void
  onCancel: () => void
  packs: string[]
}) {
  const [draft, setDraft] = useState<PointDefinition | null>(null)
  const [pdfText, setPdfText] = useState("")
  const [error, setError] = useState<string>()
  useEffect(() => {
    setDraft(point ? structuredClone(point) : null)
    setPdfText(point?.sourcePdfPages?.join(", ") ?? "")
    setError(undefined)
  }, [point])
  const setField = (key: string, value: unknown) =>
    setDraft((old) => {
      if (!old) return old
      const next = { ...old }
      if (value === undefined) delete next[key]
      else next[key] = value
      return next
    })
  const textField = (key: string, label: string, multiline = false) => (
    <Field label={label} full={multiline} key={key}>
      {multiline ? (
        <Input.TextArea
          aria-label={label}
          rows={3}
          value={String(draft?.[key] ?? "")}
          onChange={(event) => setField(key, event.target.value)}
        />
      ) : (
        <Input
          aria-label={label}
          value={String(draft?.[key] ?? "")}
          onChange={(event) =>
            setField(
              key,
              event.target.value ||
                (key === "id" ||
                key === "section" ||
                key === "label" ||
                key === "reference" ||
                key === "unit"
                  ? ""
                  : undefined),
            )
          }
        />
      )}
    </Field>
  )
  const save = () => {
    if (!draft) return
    const result = structuredClone(draft)
    if (pdfText.trim()) {
      const pages = pdfText
        .split(/[\s,;]+/)
        .filter(Boolean)
        .map(Number)
      if (pages.some((page) => !Number.isSafeInteger(page) || page < 1)) {
        setError(
          "Les pages PDF doivent être des nombres entiers positifs, séparés par des virgules.",
        )
        return
      }
      result.sourcePdfPages = pages
    } else if (draft.sourcePdfPages !== undefined) result.sourcePdfPages = []
    if (!result.label.trim() || !/^[a-zA-Z0-9_-]{1,100}$/.test(result.id)) {
      setError(
        "Renseignez un libellé et un identifiant composé de lettres, chiffres, tirets ou underscores.",
      )
      return
    }
    onSave(result)
  }
  return (
    <Modal
      title="Définition du point de contrôle"
      open={!!point}
      width={950}
      onCancel={onCancel}
      onOk={save}
      okText="Conserver ce point"
      cancelText="Annuler"
      destroyOnClose
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      {draft && (
        <div className="regulatory-form-grid">
          {textField("id", "Identifiant stable du point")}
          {textField("controlCode", "Code de contrôle")}
          {textField("section", "Section")}
          {textField("label", "Libellé")}
          <Field label="Nature">
            <Select
              aria-label="Nature"
              allowClear
              showSearch
              value={draft.nature}
              options={natureOptions}
              onChange={(value) => setField("nature", value)}
            />
          </Field>
          {textField("reference", "Référence affichée")}
          {textField("applicability", "Applicabilité", true)}
          {textField("method", "Méthode", true)}
          {textField("expected", "Résultat attendu", true)}
          {textField("unit", "Unité / description de mesure")}
          <Field label="Justificatif">
            <Select
              aria-label="Justificatif"
              value={
                draft.evidenceRequired === undefined
                  ? "unknown"
                  : draft.evidenceRequired
                    ? "required"
                    : "optional"
              }
              options={[
                { label: "Non renseigné", value: "unknown" },
                { label: "Requis", value: "required" },
                { label: "Non requis", value: "optional" },
              ]}
              onChange={(value) =>
                setField("evidenceRequired", value === "unknown" ? undefined : value === "required")
              }
            />
          </Field>
          <Field label="Photo requise">
            <Switch
              aria-label="Photo requise"
              checked={draft.photoRequired}
              onChange={(value) => setField("photoRequired", value)}
            />
          </Field>
          <Field label="Mesure requise">
            <Switch
              aria-label="Mesure requise"
              checked={draft.measurementRequired}
              onChange={(value) => setField("measurementRequired", value)}
            />
          </Field>
          {textField("interpretationNote", "Note d’interprétation", true)}
          {(
            [
              ["interpretationReserve", "Réserve d’interprétation"],
              ["coverageGap", "Complément absent"],
            ] as const
          ).map(([key, label]) => (
            <Field key={key} label={label}>
              <Select
                aria-label={label}
                value={draft[key] === undefined ? "unknown" : draft[key] ? "yes" : "no"}
                options={[
                  { value: "unknown", label: "Non renseigné" },
                  { value: "yes", label: "Oui" },
                  { value: "no", label: "Non" },
                ]}
                onChange={(value) =>
                  setField(key, value === "unknown" ? undefined : value === "yes")
                }
              />
            </Field>
          ))}
          {textField("missingPart", "Partie manquante")}
          {textField("sourceDocument", "Désignation du document source")}
          {textField("sourceClause", "Clause source")}
          {textField("sourcePages", "Pages source (texte)")}
          <Field label="Pages PDF">
            <Input
              aria-label="Pages PDF"
              placeholder="13, 24"
              value={pdfText}
              onChange={(event) => setPdfText(event.target.value)}
            />
          </Field>
          <Field label="Partie source">
            <InputNumber
              aria-label="Partie source"
              min={1}
              precision={0}
              value={draft.sourcePart}
              onChange={(value) => setField("sourcePart", value ?? undefined)}
              style={{ width: "100%" }}
            />
          </Field>
          {textField("sourceFile", "Nom du fichier source")}
          <Field label="Paquet source">
            <Select
              aria-label="Paquet source du point"
              allowClear
              showSearch
              value={draft.sourcePack}
              options={packs.map((value) => ({ value, label: value }))}
              onChange={(value) => setField("sourcePack", value)}
            />
          </Field>
          <Field label="Références associées" full>
            <Select
              aria-label="Références associées"
              mode="tags"
              value={draft.relatedReferences ?? []}
              onChange={(value) => setField("relatedReferences", value)}
            />
          </Field>
        </div>
      )}
    </Modal>
  )
}
function IncludeRow({
  link,
  onChange,
}: {
  link: CatalogInclude
  onChange: (uuid: string) => void
}) {
  const [page, setPage] = useState(1)
  const resource = useCatalogResource(
    useCallback(
      async (signal) => {
        const [revisions, pinned] = await Promise.all([
          regulatoryApi.revisions(
            link.templateUuid,
            { limit: 25, offset: (page - 1) * 25 },
            signal,
          ),
          link.revisionUuid
            ? regulatoryApi.revision(link.templateUuid, link.revisionUuid, signal)
            : Promise.resolve(null),
        ])
        return { revisions, pinned }
      },
      [link.templateUuid, link.revisionUuid, page],
    ),
  )
  const options =
    resource.data?.revisions.items.map((revision) => ({
      value: revision.uuid,
      label: `v${revision.sourceVersion} · ${scopeLabels[revision.definition.scope]}`,
    })) ?? []
  if (
    resource.data?.pinned &&
    !options.some((option) => option.value === resource.data?.pinned?.uuid)
  )
    options.unshift({
      value: resource.data.pinned.uuid,
      label: `v${resource.data.pinned.sourceVersion} · ${scopeLabels[resource.data.pinned.definition.scope]}`,
    })
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Link
        to={
          link.revisionUuid
            ? `/regulatory/templates/${link.templateUuid}/revisions/${link.revisionUuid}`
            : `/regulatory/templates/${link.templateUuid}`
        }
      >
        {resource.data?.pinned?.definition.name ?? "Module inclus"}
      </Link>
      <Select
        aria-label="Révision du module inclus"
        loading={resource.loading}
        value={link.revisionUuid ?? undefined}
        placeholder="Révision à préciser"
        options={options}
        onChange={onChange}
        style={{ width: "100%" }}
      />
      {(resource.data?.revisions.total ?? 0) > 25 && (
        <Space>
          <Button size="small" disabled={page === 1} onClick={() => setPage((value) => value - 1)}>
            Plus récentes
          </Button>
          <Button
            size="small"
            disabled={page * 25 >= (resource.data?.revisions.total ?? 0)}
            onClick={() => setPage((value) => value + 1)}
          >
            Plus anciennes
          </Button>
        </Space>
      )}
      {resource.error && <Typography.Text type="danger">{resource.error.message}</Typography.Text>}
    </Space>
  )
}
export function TemplateEditorPage() {
  const { templateUuid } = useParams<{ templateUuid?: string }>()
  const { user } = useAuthContext()
  const { message, modal } = App.useApp()
  const history = useHistory()
  const initial = useCatalogResource(
    useCallback(
      (signal) =>
        templateUuid ? regulatoryApi.template(templateUuid, signal) : Promise.resolve(null),
      [templateUuid],
    ),
  )
  const selectors = useCatalogResource(
    useCallback(async (signal) => {
      const [templates, packs] = await Promise.all([
        allCatalogPages(regulatoryApi.templates, signal),
        allCatalogPages(regulatoryApi.packs, signal),
      ])
      return { templates, packs }
    }, []),
  )
  const [definition, setDefinition] = useState<TemplateDefinition>(emptyDefinition)
  const [includes, setIncludes] = useState<CatalogInclude[]>([])
  const [base, setBase] = useState<CatalogRevision>()
  const [dirty, setDirty] = useState(false)
  const [review, setReview] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [conflict, setConflict] = useState(false)
  const [latest, setLatest] = useState<CatalogRevision | null>(null)
  const [latestLoading, setLatestLoading] = useState(false)
  const [pointIndex, setPointIndex] = useState<number | null>(null)
  const [editingPoint, setEditingPoint] = useState<PointDefinition | null>(null)
  const [moduleUuid, setModuleUuid] = useState<string>()
  const [moduleRevisionUuid, setModuleRevisionUuid] = useState<string>()
  const [modulePage, setModulePage] = useState(1)
  const initialized = useRef<string>()
  const routeKey = templateUuid ?? "new"
  useEffect(() => {
    if (!dirty) return
    const guard = (event: BeforeUnloadEvent) => {
      if (initialized.current !== "committed") {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", guard)
    return () => window.removeEventListener("beforeunload", guard)
  }, [dirty])
  useEffect(() => {
    if (initialized.current === routeKey || initial.loading || initial.error) return
    if (templateUuid && initial.data?.template.uuid !== templateUuid) return
    initialized.current = routeKey
    setDefinition(
      initial.data ? structuredClone(initial.data.revision.definition) : emptyDefinition(),
    )
    setIncludes(initial.data ? structuredClone(initial.data.revision.includes) : [])
    setBase(initial.data?.revision)
    setDirty(false)
    setReview(false)
    setErrors([])
    setConflict(false)
    setLatest(null)
  }, [initial.data, initial.loading, initial.error, routeKey, templateUuid])
  const modules = useCatalogResource(
    useCallback(
      (signal) =>
        moduleUuid
          ? regulatoryApi.revisions(
              moduleUuid,
              { limit: 25, offset: (modulePage - 1) * 25 },
              signal,
            )
          : Promise.resolve({ items: [], total: 0, limit: 25, offset: 0 }),
      [moduleUuid, modulePage],
    ),
  )
  const permissions = catalogPermissions(user, initial.data?.template)
  const setField = (key: string, value: unknown) => {
    setDefinition((old) => {
      const next = { ...old }
      if (value === undefined) delete next[key]
      else next[key] = value
      return next
    })
    setDirty(true)
  }
  const savePoint = (point: PointDefinition) => {
    const next = [...definition.points]
    if (pointIndex === null) next.push(point)
    else next[pointIndex] = point
    setField("points", next)
    setEditingPoint(null)
  }
  const updateIncludes = (next: CatalogInclude[]) => {
    setIncludes(next)
    setDirty(true)
  }
  const save = async () => {
    const validation = validateDefinition(definition, includes)
    if (validation.length) {
      setErrors(validation)
      return
    }
    setErrors([])
    setSaving(true)
    try {
      const payload = makeCatalogWrite(definition, includes, base, review)
      const result = templateUuid
        ? await regulatoryApi.revise(templateUuid, payload)
        : await regulatoryApi.create(payload)
      setDirty(false)
      message.success(`Version ${result.sourceVersion} enregistrée`)
      // Prompt sees the committed flag before navigation.
      initialized.current = "committed"
      setTimeout(() => history.push(`/regulatory/templates/${result.templateUuid}`), 0)
    } catch (error) {
      setErrors([error instanceof Error ? error.message : "Enregistrement impossible"])
      setConflict(error instanceof RegulatoryApiError && error.status === 409)
    } finally {
      setSaving(false)
    }
  }
  const fetchLatest = async () => {
    if (!templateUuid) return
    setLatestLoading(true)
    try {
      setLatest((await regulatoryApi.template(templateUuid)).revision)
    } catch (error) {
      setErrors([error instanceof Error ? error.message : "Chargement impossible"])
    } finally {
      setLatestLoading(false)
    }
  }
  const rebase = () => {
    if (!latest) return
    modal.confirm({
      title: "Utiliser cette nouvelle base ?",
      content:
        "Votre saisie complète sera conservée. Les différences affichées remplaceront celles de la version récente lors de votre prochain enregistrement.",
      okText: "Conserver ma saisie sur cette base",
      cancelText: "Annuler",
      onOk: () => {
        setBase(latest)
        setConflict(false)
        setLatest(null)
        setErrors([])
        setDirty(true)
      },
    })
  }
  const addModule = () => {
    if (!moduleUuid || !moduleRevisionUuid) return
    updateIncludes([...includes, { templateUuid: moduleUuid, revisionUuid: moduleRevisionUuid }])
    setModuleUuid(undefined)
    setModuleRevisionUuid(undefined)
    setModulePage(1)
  }
  if (initial.loading || initial.error)
    return (
      <CatalogFrame title="Éditeur de modèle">
        <ResourceState {...initial} retry={initial.reload}>
          {null}
        </ResourceState>
      </CatalogFrame>
    )
  if (!permissions.manage || (templateUuid && !permissions.edit))
    return (
      <CatalogFrame title="Éditeur de modèle">
        <Alert
          type="info"
          showIcon
          message="Ce modèle est en lecture seule pour votre compte."
          description={
            <Link
              to={templateUuid ? `/regulatory/templates/${templateUuid}` : "/regulatory/templates"}
            >
              Consulter le catalogue
            </Link>
          }
        />
      </CatalogFrame>
    )
  if (initial.data?.template.archived)
    return (
      <CatalogFrame title="Éditeur de modèle">
        <Alert type="warning" message="Restaurez le modèle avant de créer une nouvelle révision." />
      </CatalogFrame>
    )
  const nextVersion = base ? base.sourceVersion + 1 : 1
  return (
    <CatalogFrame
      title={templateUuid ? `Nouvelle révision · version ${nextVersion}` : "Nouveau modèle"}
    >
      <Prompt
        when={dirty && initialized.current !== "committed"}
        message="Quitter l’éditeur et perdre les modifications non enregistrées ?"
      />
      {base && (
        <Alert
          type="info"
          showIcon
          message={`Vous préparez la version ${nextVersion}. La version ${base.sourceVersion} restera inchangée.`}
        />
      )}
      {!!errors.length && (
        <Alert
          showIcon
          type="error"
          message="Enregistrement impossible"
          description={
            <ul>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          }
        />
      )}
      {conflict && (
        <Alert
          type="warning"
          showIcon
          message="Votre saisie est conservée"
          description="Vérifiez le motif du refus. Vous pouvez charger la version actuelle pour examiner les différences avant de reprendre l’enregistrement."
          action={
            <Button loading={latestLoading} onClick={fetchLatest}>
              Comparer à la version actuelle
            </Button>
          }
        />
      )}
      <Card title="Présentation du modèle">
        <div className="regulatory-form-grid">
          <Field label="Nom">
            <Input
              aria-label="Nom du modèle"
              value={definition.name}
              onChange={(event) => setField("name", event.target.value)}
            />
          </Field>
          <Field label="Périmètre">
            <Select
              aria-label="Périmètre du modèle"
              value={definition.scope}
              options={Object.entries(scopeLabels).map(([value, label]) => ({ value, label }))}
              onChange={(value) => setField("scope", value)}
            />
          </Field>
          <Field label="Référence" full>
            <Input
              aria-label="Référence du modèle"
              value={definition.reference}
              onChange={(event) => setField("reference", event.target.value)}
            />
          </Field>
          <Field label="Description" full>
            <Input.TextArea
              aria-label="Description du modèle"
              rows={4}
              value={definition.description}
              onChange={(event) => setField("description", event.target.value)}
            />
          </Field>
          <Field label="Couverture" full>
            <Input.TextArea
              aria-label="Couverture du modèle"
              rows={2}
              value={definition.coverage ?? ""}
              onChange={(event) => setField("coverage", event.target.value || undefined)}
            />
          </Field>
          <Field label="Parties manquantes">
            <Select
              aria-label="Parties manquantes"
              mode="tags"
              value={definition.missingParts ?? []}
              onChange={(value) => setField("missingParts", value)}
            />
          </Field>
          <Field label="Paquet source">
            <Select
              aria-label="Paquet source du modèle"
              allowClear
              loading={selectors.loading}
              value={definition.sourcePack}
              options={selectors.data?.packs.map((pack) => ({
                value: pack.externalId,
                label: pack.externalId,
              }))}
              onChange={(value) => setField("sourcePack", value)}
            />
          </Field>
        </div>
      </Card>
      <Card
        title={`Points propres (${definition.points.length})`}
        extra={
          <Button
            icon={<PlusOutlined />}
            onClick={() => {
              setPointIndex(null)
              setEditingPoint(newPoint())
            }}
          >
            Ajouter un point
          </Button>
        }
      >
        <Table<PointDefinition>
          rowKey="id"
          dataSource={definition.points}
          pagination={false}
          scroll={{ x: 650 }}
          columns={[
            { title: "Ordre", width: 65, render: (_value, _point, index) => index + 1 },
            { title: "Section", dataIndex: "section" },
            {
              title: "Point",
              render: (_value, point) => (
                <Space direction="vertical">
                  <Typography.Text>{point.label}</Typography.Text>
                  <NatureTag point={point} />
                </Space>
              ),
            },
            {
              title: "Actions",
              width: 190,
              render: (_value, point, index) => (
                <Space>
                  <Button
                    aria-label={`Monter ${point.label}`}
                    icon={<ArrowUpOutlined />}
                    disabled={index === 0}
                    onClick={() => setField("points", moveItem(definition.points, index, -1))}
                  />
                  <Button
                    aria-label={`Descendre ${point.label}`}
                    icon={<ArrowDownOutlined />}
                    disabled={index === definition.points.length - 1}
                    onClick={() => setField("points", moveItem(definition.points, index, 1))}
                  />
                  <Button
                    aria-label={`Modifier ${point.label}`}
                    icon={<EditOutlined />}
                    onClick={() => {
                      setPointIndex(index)
                      setEditingPoint(point)
                    }}
                  />
                  <Button
                    aria-label={`Retirer ${point.label}`}
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      setField(
                        "points",
                        definition.points.filter((_row, rowIndex) => rowIndex !== index),
                      )
                    }
                  />
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Card title={`Modules inclus (${includes.length})`}>
        <Alert
          showIcon
          type="info"
          message="Chaque module est lié à une révision précise. Son ordre est conservé."
          style={{ marginBottom: 16 }}
        />
        {selectors.error && (
          <Alert
            type="error"
            message={selectors.error.message}
            action={<Button onClick={selectors.reload}>Réessayer</Button>}
          />
        )}
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            aria-label="Module à inclure"
            showSearch
            optionFilterProp="label"
            placeholder="Choisir un module"
            value={moduleUuid}
            onChange={(value) => {
              setModuleUuid(value)
              setModuleRevisionUuid(undefined)
              setModulePage(1)
            }}
            loading={selectors.loading}
            options={selectors.data?.templates
              .filter(
                (template) =>
                  template.uuid !== templateUuid &&
                  !includes.some((link) => link.templateUuid === template.uuid),
              )
              .map((template: CatalogTemplate) => ({
                value: template.uuid,
                label: `${template.name} · ${scopeLabels[template.scope]}`,
              }))}
            style={{ width: 330, maxWidth: "100%" }}
          />
          <Select
            aria-label="Révision du module à inclure"
            placeholder="Choisir sa révision"
            value={moduleRevisionUuid}
            loading={modules.loading}
            onChange={setModuleRevisionUuid}
            options={modules.data?.items.map((revision) => ({
              value: revision.uuid,
              label: `v${revision.sourceVersion} · ${scopeLabels[revision.definition.scope]}`,
              disabled: (revision.definition.scope === "site") !== (definition.scope === "site"),
            }))}
            style={{ width: 230 }}
          />
          <Button disabled={!moduleUuid || !moduleRevisionUuid} onClick={addModule}>
            Inclure
          </Button>
          {(modules.data?.total ?? 0) > 25 && (
            <Space>
              <Button
                disabled={modulePage === 1}
                onClick={() => setModulePage((value) => value - 1)}
              >
                Plus récentes
              </Button>
              <Button
                disabled={modulePage * 25 >= (modules.data?.total ?? 0)}
                onClick={() => setModulePage((value) => value + 1)}
              >
                Plus anciennes
              </Button>
            </Space>
          )}
        </Space>
        {modules.error && <Alert type="error" message={modules.error.message} />}
        <Table<CatalogInclude>
          rowKey="templateUuid"
          dataSource={includes}
          pagination={false}
          columns={[
            { title: "Ordre", width: 70, render: (_value, _link, index) => index + 1 },
            {
              title: "Module et révision",
              render: (_value, link, index) => (
                <IncludeRow
                  link={link}
                  onChange={(revisionUuid) =>
                    updateIncludes(
                      includes.map((old, oldIndex) =>
                        oldIndex === index ? { ...old, revisionUuid } : old,
                      ),
                    )
                  }
                />
              ),
            },
            {
              title: "Actions",
              width: 145,
              render: (_value, _link, index) => (
                <Space>
                  <Button
                    aria-label="Monter le module"
                    icon={<ArrowUpOutlined />}
                    disabled={index === 0}
                    onClick={() => updateIncludes(moveItem(includes, index, -1))}
                  />
                  <Button
                    aria-label="Descendre le module"
                    icon={<ArrowDownOutlined />}
                    disabled={index === includes.length - 1}
                    onClick={() => updateIncludes(moveItem(includes, index, 1))}
                  />
                  <Button
                    aria-label="Retirer le module"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      updateIncludes(includes.filter((_row, rowIndex) => rowIndex !== index))
                    }
                  />
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Collapse
        items={[
          {
            key: "preview",
            label: "Comparer le brouillon à sa révision de base",
            children: base ? (
              <RevisionComparison
                before={base.definition}
                after={{ ...definition, version: nextVersion }}
                beforeIncludes={base.includes}
                afterIncludes={includes}
              />
            ) : (
              <Typography.Paragraph>
                La première version n’a pas de révision précédente.
              </Typography.Paragraph>
            ),
          },
        ]}
      />
      <div className="regulatory-editor-footer">
        <Checkbox
          checked={review}
          onChange={(event) => {
            setReview(event.target.checked)
            setDirty(true)
          }}
        >
          Déclarer une revue interne de cette définition
        </Checkbox>
        <Typography.Text type="secondary">
          Le serveur enregistre le compte et la date de revue. L’approbation précédente n’est pas
          héritée.
        </Typography.Text>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={selectors.loading || !!selectors.error}
          onClick={save}
        >
          Enregistrer la version {nextVersion}
        </Button>
        <Link to={templateUuid ? `/regulatory/templates/${templateUuid}` : "/regulatory/templates"}>
          <Button disabled={saving}>Annuler</Button>
        </Link>
      </div>
      <PointEditor
        point={editingPoint}
        onSave={savePoint}
        onCancel={() => setEditingPoint(null)}
        packs={selectors.data?.packs.map((pack) => pack.externalId) ?? []}
      />
      <Modal
        title="Version actuelle et votre brouillon"
        open={!!latest}
        onCancel={() => setLatest(null)}
        width={1100}
        footer={
          <Space>
            <Button onClick={() => setLatest(null)}>Fermer</Button>
            <Button type="primary" onClick={rebase}>
              Utiliser cette base et conserver ma saisie
            </Button>
          </Space>
        }
        destroyOnClose
      >
        {latest && (
          <RevisionComparison
            before={latest.definition}
            after={{ ...definition, version: latest.sourceVersion + 1 }}
            beforeIncludes={latest.includes}
            afterIncludes={includes}
          />
        )}
      </Modal>
    </CatalogFrame>
  )
}
